import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, ShoppingCart, CreditCard, User, QrCode } from "lucide-react";

export default function MobileNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);

  const routes = [
    { path: "/", label: "Inicio", icon: Home },
    { path: "/sucursales", label: "Carrito", icon: ShoppingCart },
    { path: "/pedidos", label: "Pedidos", icon: CreditCard, badge: 2 },
    { path: "/profile", label: "Perfil", icon: User },
  ];

  useEffect(() => {
    const currentIndex = routes.findIndex(
      (route) => route.path === location.pathname
    );
    if (currentIndex !== -1) {
      setActiveIndex(currentIndex);
    }
  }, [location.pathname]);

  const handleNavClick = (path, index) => {
    setActiveIndex(index);
    navigate(path);
  };

  const handleQrClick = () => {
    navigate("/qr");
  };

  return (
    <>
      {/* Safe area spacer */}
      <div className="h-24 sm:h-28"></div>

      {/* Navbar Container */}
      <div className="fixed bottom-0 left-0 right-0 z-50 pb-safe">
        {/* Backdrop blur effect */}
        <div className="absolute inset-0 bg-white/90 backdrop-blur-xl border-t border-gray-200/50 shadow-2xl"></div>

        {/* Active indicator slider */}
        <div
          className="absolute top-0 h-1 bg-gradient-to-r from-[#FF0000] via-[#FFBC00] to-[#FF0000] rounded-full transition-all duration-300 ease-out"
          style={{
            left: `${
              activeIndex < 2 ? activeIndex * 25 : (activeIndex + 1) * 20
            }%`,
            width: activeIndex < 2 ? "25%" : "20%",
          }}
        ></div>

        <div className="relative px-2 sm:px-4 pt-2">
          {/* Navigation Items Container */}
          <div className="flex justify-around items-end h-20 relative">
            {routes.map((route, index) => {
              const isActive = activeIndex === index;
              const Icon = route.icon;

              // Add spacer for QR button
              if (index === 2) {
                return (
                  <React.Fragment key={`nav-${index}`}>
                    {/* Spacer for center QR button */}
                    <div className="w-16 sm:w-20"></div>

                    {/* Nav item */}
                    <button
                      onClick={() => handleNavClick(route.path, index)}
                      className="relative flex flex-col items-center justify-end pb-3 flex-1 group"
                      aria-label={route.label}
                    >
                      {/* Icon container with animation */}
                      <div className="relative mb-1">
                        {/* Active background pill */}
                        <div
                          className={`absolute inset-0 -inset-x-3 -inset-y-2 bg-gradient-to-br from-[#FF0000]/10 to-[#FFBC00]/10 rounded-2xl transition-all duration-300 ${
                            isActive
                              ? "scale-100 opacity-100"
                              : "scale-0 opacity-0"
                          }`}
                        ></div>

                        {/* Icon */}
                        <Icon
                          size={24}
                          strokeWidth={isActive ? 2.5 : 2}
                          className={`relative transition-all duration-300 ${
                            isActive
                              ? "text-[#FF0000] scale-110 -translate-y-1"
                              : "text-gray-600 group-hover:text-[#FF0000] group-hover:scale-105"
                          }`}
                        />

                        {/* Badge */}
                        {route.badge && (
                          <span className="absolute -top-1 -right-2 bg-gradient-to-br from-[#FF0000] to-[#FF0000]/80 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full shadow-lg ring-2 ring-white">
                            {route.badge}
                          </span>
                        )}
                      </div>

                      {/* Label */}
                      <span
                        className={`text-xs font-semibold transition-all duration-300 ${
                          isActive
                            ? "text-[#FF0000] opacity-100 scale-100"
                            : "text-gray-600 opacity-70 scale-95 group-hover:opacity-100 group-hover:text-[#FF0000]"
                        }`}
                      >
                        {route.label}
                      </span>

                      {/* Active dot indicator */}
                      <div
                        className={`absolute bottom-1 w-1 h-1 bg-[#FF0000] rounded-full transition-all duration-300 ${
                          isActive
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-0"
                        }`}
                      ></div>
                    </button>
                  </React.Fragment>
                );
              }

              return (
                <button
                  key={index}
                  onClick={() => handleNavClick(route.path, index)}
                  className="relative flex flex-col items-center justify-end pb-3 flex-1 group"
                  aria-label={route.label}
                >
                  {/* Icon container with animation */}
                  <div className="relative mb-1">
                    {/* Active background pill */}
                    <div
                      className={`absolute inset-0 -inset-x-3 -inset-y-2 bg-gradient-to-br from-[#FF0000]/10 to-[#FFBC00]/10 rounded-2xl transition-all duration-300 ${
                        isActive ? "scale-100 opacity-100" : "scale-0 opacity-0"
                      }`}
                    ></div>

                    {/* Icon */}
                    <Icon
                      size={24}
                      strokeWidth={isActive ? 2.5 : 2}
                      className={`relative transition-all duration-300 ${
                        isActive
                          ? "text-[#FF0000] scale-110 -translate-y-1"
                          : "text-gray-600 group-hover:text-[#FF0000] group-hover:scale-105"
                      }`}
                    />

                    {/* Badge */}
                    {route.badge && (
                      <span className="absolute -top-1 -right-2 bg-gradient-to-br from-[#FF0000] to-[#FF0000]/80 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full shadow-lg ring-2 ring-white">
                        {route.badge}
                      </span>
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={`text-xs font-semibold transition-all duration-300 ${
                      isActive
                        ? "text-[#FF0000] opacity-100 scale-100"
                        : "text-gray-600 opacity-70 scale-95 group-hover:opacity-100 group-hover:text-[#FF0000]"
                    }`}
                  >
                    {route.label}
                  </span>

                  {/* Active dot indicator */}
                  <div
                    className={`absolute bottom-1 w-1 h-1 bg-[#FF0000] rounded-full transition-all duration-300 ${
                      isActive ? "opacity-100 scale-100" : "opacity-0 scale-0"
                    }`}
                  ></div>
                </button>
              );
            })}
          </div>

          {/* Floating QR Button */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-8">
            <button
              onClick={handleQrClick}
              className="relative bg-gradient-to-br from-[#FF0000] via-[#FF0000] to-[#CC0000] w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl border-4 border-white transition-colors duration-300 hover:from-[#FFBC00] hover:via-[#FF0000] hover:to-[#FFBC00]"
              aria-label="Escanear código QR"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#FFBC00] to-[#FF0000] rounded-2xl blur-xl opacity-50"></div>

              {/* Icon */}
              <QrCode
                className="relative w-8 h-8 text-white"
                strokeWidth={2.5}
              />
            </button>
          </div>
        </div>

        {/* Bottom safe area for devices with notch */}
        <div className="h-2"></div>
      </div>
    </>
  );
}
